import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  CreateSaleCommissionRuleDto,
  SaleCommissionConditionEntity,
  SaleCommissionRuleRoleEntity,
  SaleCommissionRuleTargetEntity,
  SaleCommissionRuleDetailEntity,
  SaleCommissionRuleEntity,
  SaleCommissionTargetType,
  UpdateSaleCommissionRuleDto,
  UpdateSaleCommissionStatusDto,
} from './model'

type GetSaleCommissionRulesParams = PaginationOpt & {
  targetType: SaleCommissionTargetType
  roleId?: number
  presetCodes?: string[]
}

const deserializeConditions = (value: unknown): SaleCommissionConditionEntity[] =>
  Array.isArray(value)
    ? value.map((condition) =>
        deserialize(condition as Record<string, unknown>, SaleCommissionConditionEntity)
      )
    : []

const deserializeRoles = (value: unknown): SaleCommissionRuleRoleEntity[] =>
  Array.isArray(value)
    ? value.map((role) =>
        deserialize(role as Record<string, unknown>, SaleCommissionRuleRoleEntity)
      )
    : []

const deserializeTargets = (value: unknown): SaleCommissionRuleTargetEntity[] =>
  Array.isArray(value)
    ? value.map((target) =>
        deserialize(target as Record<string, unknown>, SaleCommissionRuleTargetEntity)
      )
    : []

const deserializeSaleCommissionRule = (item: Record<string, unknown>): SaleCommissionRuleEntity => {
  const entity = deserialize(item, SaleCommissionRuleEntity)

  entity.roles = deserializeRoles(item.roles)
  entity.conditions = deserializeConditions(item.conditions)
  entity.targets = deserializeTargets(item.targets)

  return entity
}

const deserializeSaleCommissionRuleDetail = (
  item: Record<string, unknown>
): SaleCommissionRuleDetailEntity => {
  const entity = deserialize(item, SaleCommissionRuleDetailEntity)

  entity.targets = deserializeTargets(item.targets)
  entity.roles = deserializeRoles(item.roles)
  entity.conditions = deserializeConditions(item.conditions)

  return entity
}

export const SaleCommissionService = (client: AxiosInstance) => {
  return {
    async getSaleCommissionRules({
      page = 1,
      pageSize = 10,
      query,
      targetType,
      roleId,
      presetCodes,
    }: GetSaleCommissionRulesParams): Promise<PaginationResponse<SaleCommissionRuleEntity>> {
      const params = {
        page,
        pageSize,
        q: query,
        targetType,
        roleId,
        presetCodes,
      }

      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/sale-commissions',
          { params, paramsSerializer: { indexes: null } }
        )
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item: Record<string, unknown>) => deserializeSaleCommissionRule(item)) ??
          [],
      }
    },

    async updateSaleCommissionStatus(
      id: string,
      payload: UpdateSaleCommissionStatusDto
    ): Promise<SaleCommissionRuleEntity> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/sale-commissions/${id}/status`, payload)
      )

      return deserializeSaleCommissionRule(data)
    },

    async createSaleCommissionRule(
      payload: CreateSaleCommissionRuleDto
    ): Promise<SaleCommissionRuleDetailEntity> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/sale-commissions', payload)
      )

      return deserializeSaleCommissionRuleDetail(data)
    },

    async getSaleCommissionRuleById(id: string): Promise<SaleCommissionRuleDetailEntity> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/sale-commissions/${id}`)
      )

      return deserializeSaleCommissionRuleDetail(data)
    },

    async updateSaleCommissionRule(
      id: string,
      payload: UpdateSaleCommissionRuleDto
    ): Promise<SaleCommissionRuleDetailEntity> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/sale-commissions/${id}`, payload)
      )

      return deserializeSaleCommissionRuleDetail(data)
    },

    async deleteSaleCommissionRule(id: string): Promise<void> {
      await wrapApiCall(() => client.delete(`/sale-commissions/${id}`))
    },
  }
}
